import React from 'react';
import { Box, Typography, Button, Container, Grid, Chip, Stack, Skeleton } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import BoltIcon from '@mui/icons-material/Bolt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { scrollToSection } from '../utils/utils';

const HeroSection = ({ data }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const isLoading = data === undefined;

    return (
        <Box
            id="home"
            sx={{
                background: theme.palette.custom.heroGradient,
                color: theme.palette.text.white,
                pt: 10,
                pb: 15,
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    {/* Trusted Badge */}
                    <Chip
                        icon={<FiberManualRecordIcon />}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <BoltIcon
                                    sx={{
                                        fontSize: 18,
                                        color: '#FFD700',                         // golden yellow like in image
                                        filter: 'drop-shadow(0 0 4px #FFD700)',   // subtle glow
                                    }}
                                />
                                {t('pages.home.hero.trusted_badge')}
                            </Box>
                        }
                        sx={{
                            backgroundColor: theme.palette.custom.heroBadgeBg,
                            color: theme.palette.custom.white,
                            backdropFilter: 'blur(10px)',
                            mb: 4,
                            px: 1,
                            py: { xs: 0.5, sm: 0 },
                            height: "auto",
                            minHeight: "38px",
                            border: `1px solid ${theme.palette.custom.heroBorder}`,
                            lineHeight: "1.25rem",
                            borderRadius: '26px',

                            '& .MuiChip-icon': {
                                color: theme.palette.secondary.light,
                                fontSize: 12,
                                animation: 'pulse 2s cubic-bezier(.4, 0, .6, 1) infinite',
                            },
                            '@keyframes pulse': {
                                '0%, 100%': {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                },
                                '50%': {
                                    opacity: 0.5,
                                    transform: 'scale(1.15)',
                                },
                            },
                            '& .MuiChip-label': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: 500,
                                letterSpacing: '0.02em',
                                whiteSpace: 'normal',
                                display: 'block',
                                textAlign: 'center',
                            },
                        }}
                    />

                    {/* Main Headline */}
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                            fontSize: {
                                xs: '2.6rem',   // mobile
                                sm: '3.5rem',   // tablets
                                md: '4.5rem',   // desktop
                            },
                            lineHeight: 1.1,
                        }}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Skeleton variant="text" height={60} width="80%" />
                            </Box>
                        ) : (
                            data?.meta?.title1 || t('pages.home.hero.headline_prefix')
                        )}<br />
                        <Box
                            component="span"
                            sx={{
                                background: theme.palette.custom.accentGradient,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                textFillColor: 'transparent',
                            }}
                        >
                            {isLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Skeleton variant="text" height={60} width="60%" />
                                </Box>
                            ) : (
                                data?.meta?.title2 || t('pages.home.hero.headline_highlight')
                            )}
                        </Box>
                    </Typography>

                    {/* Subheadline */}
                    <Typography component="div" sx={{color: theme.palette.custom.white, fontSize: '1.25rem', mb: 4, maxWidth: '650px', mx: 'auto', opacity: 0.9, fontWeight: 'normal', lineHeight: 1.50 }}>
                    {isLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Skeleton variant="text" height={40} width="90%" />
                        </Box>
                    ) : (
                        data?.meta?.description || t('pages.home.hero.subheadline')
                    )}
                </Typography>

                {/* Action Buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 6 }}>
                    <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => {
                            scrollToSection("portfolio");
                        }}
                        sx={{
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.primary.main,
                            py: 1,
                            borderRadius: '12px',
                            fontSize: '1.12rem',
                            letterSpacing: 0,
                            px: 4,
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgb(0 0 0 / .1), 0 4px 6px -4px rgb(0 0 0 / .1)',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        {t('pages.home.hero.view_work')}
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        sx={{
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.primary.main,
                            px: 4,
                            py: 1,
                            borderRadius: '12px',
                            fontSize: '1.12rem',
                            fontWeight: 600,
                            letterSpacing: 0,
                            boxShadow: '0 10px 15px -3px rgb(0 0 0 / .1), 0 4px 6px -4px rgb(0 0 0 / .1)',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                        onClick={() => navigate('/engineers')}
                    >
                        {t('pages.home.hero.start_project')}
                    </Button>
                </Stack>

                {/* Stats Cards */}
                {/* CLIENT + DURATION */}
                {isLoading ? (
                    <Grid container spacing={3} mt={5} justifyContent="center">
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Skeleton variant="rounded" height={100} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Skeleton variant="rounded" height={100} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Skeleton variant="rounded" height={100} />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={3} justifyContent="center" sx={{ position: 'relative', zIndex: 2 }}>
                        <Grid item xs={12} sm={4} md={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    backgroundColor: theme.palette.custom.statCardBg,
                                    px: { xs: 3, sm: 4, md: 5 },
                                    py: 3,
                                    border: `1px solid ${theme.palette.custom.glassBorder}`,
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '1rem',
                                    textAlign: 'center',
                                    transition: '0.3s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.custom.statHover,
                                    },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '2.25rem',
                                        lineHeight: '2.5rem',
                                        fontWeight: 700,
                                        mb: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {data?.meta?.web_development}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ opacity: 0.8 }}
                                >
                                    {t('pages.home.hero.stats_ai_web_apps')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    backgroundColor: theme.palette.custom.statCardBg,
                                    px: { xs: 3, sm: 4, md: 5 },
                                    py: 3,
                                    border: `1px solid ${theme.palette.custom.glassBorder}`,
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '1rem',
                                    textAlign: 'center',
                                    transition: '0.3s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.custom.statHover,
                                    },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '2.25rem',
                                        lineHeight: '2.5rem',
                                        fontWeight: 700,
                                        mb: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {data?.meta?.mobile_apps}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ opacity: 0.8 }}
                                >
                                    {t('pages.home.hero.stats_vibe_coded_mobile')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    backgroundColor: theme.palette.custom.statCardBg,
                                    px: { xs: 3, sm: 4, md: 5 },
                                    py: 3,
                                    border: `1px solid ${theme.palette.custom.glassBorder}`,
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '1rem',
                                    textAlign: 'center',
                                    transition: '0.3s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.custom.statHover,
                                    },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '2.25rem',
                                        lineHeight: '2.5rem',
                                        fontWeight: 700,
                                        mb: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {data?.meta?.custom_solutions}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        opacity: 0.8
                                    }}
                                >
                                    {t('pages.home.hero.stats_intelligent_automation')}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )
                }
        </Box>
            </Container >

    {/* Gradient Divider */ }
    < Box
sx = {{
    position: 'absolute',
        bottom: 0,
            left: 0,
                right: 0,
                    height: '128px',
                        background: 'linear-gradient(to top, #f9fafb, transparent)',
                            pointerEvents: 'none',
                                zIndex: 1,
                }}
            />
        </Box >
    );
};

export default HeroSection;
